import {inject, Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {ConfigurationService} from "../../shared/services/configuration.service";
import {map, Observable} from "rxjs";
import {IAppSettingsDataInterface} from "./app-settings-data.interface";
import {
    AppSettingsOverviewServiceModel,
    FindAllByApplicationNameServiceModel,
    FindAllByApplicationNameSummaryServiceModel,
    FindApplicationSettingsCountServiceModel, ReactivateSettingModel, ReactivationsModel,
} from "./models/app-settings-service.model";
import {AppSettingsAud, AppSettingsDetail} from "../components/models/app-settings.model";
import {InactiveModeService} from "../../shared/services/inactive-mode.service";

@Injectable({
    providedIn: 'root'
})
export class AppSettingsDataService implements IAppSettingsDataInterface {
    private readonly http = inject(HttpClient);
    private readonly config = inject(ConfigurationService);
    private readonly inactiveMode = inject(InactiveModeService);

    private readonly api = this.config.configuration.api.backendUrl

    private get endpoint(): string {
        return this.inactiveMode.inactiveMode
            ? 'configuration/all-settings'
            : 'configuration/settings';
    }

    getDistinctNames(): Observable<AppSettingsOverviewServiceModel[]> {
        return this.http.get<any>(`${this.api}/${this.endpoint}/search/distinct-names?projection=names`).pipe(
            map(response => response._embedded.settings)
        );
    }

    findAllByApplicationNameSummary(applicationName: string): Observable<FindAllByApplicationNameSummaryServiceModel[]> {
        return this.http.get<any>(`${this.api}/${this.endpoint}/search/findAllByApplicationName?name=${applicationName}&projection=summary`).pipe(
            map(response => response._embedded.settings)
        );
    }

    findAllByApplicationName(applicationName: string): Observable<FindAllByApplicationNameServiceModel[]> {
        return this.http.get<any>(`${this.api}/${this.endpoint}/search/findAllByApplicationName?name=${applicationName}`).pipe(
            map(response => response._embedded.settings)
        );
    }

    getById(id: number): Observable<AppSettingsDetail> {
        return this.http.get<AppSettingsDetail>(`${this.api}/${this.endpoint}/${id}`);
    }

    searchApplicationSettings(name: string, search: string): Observable<FindAllByApplicationNameServiceModel[]> {
        return this.http.get<any>(`${this.api}/${this.endpoint}/search/searchApplicationSettings?name=${name}&search=${search}`).pipe(
            map(response => response._embedded.settings)
        );
    }

    findApplicationSettingsCount(): Observable<FindApplicationSettingsCountServiceModel[]> {
        return this.http.get<any>(`${this.api}/${this.endpoint}/search/applications-count`).pipe(
            map(response =>
                this.inactiveMode.inactiveMode
                    ? response._embedded.countAllApplicationSettingsViews
                    : response._embedded.countApplicationSettingsViews)
        );
    }

    reactivateSetting(applicationSettingId: number, reason: string): Observable<ReactivateSettingModel[]> {
        return this.http.patch<ReactivateSettingModel[]>(`${this.api}/configuration/reactivate`, {
            reason,
            applicationSettingId
        });
    }

    getReactivationsByAppSettingId(applicationSettingId: number): Observable<ReactivationsModel[]> {
        return this.http.get<any>(`${this.api}/configuration/audits/search/by-setting?id=${applicationSettingId}`).pipe(
            map(response => response._embedded.audits)
        );
    }

    getRevisionByApplicationNameAndIdentificationAndTenantName(applicationName: string, tenantName: string, identification: string): Observable<AppSettingsAud[]> {
        return this.http.get<any>(`${this.api}/configuration/aud-settings/search/findAllByApplicationNameAndIdentificationAndTenantName?name=${applicationName}&tenant=${tenantName}&identification=${identification}`).pipe(
            map(response => response._embedded.settings)
        );
    }
}
